import { Dispatch, SetStateAction } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { analysisFiles, analysisOranFiles } from '@api/schemaConfig';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { TAnalysisSchemaDTO } from '@interfaces/cmModule/dto/schemaConfigDTO';
import { IAnalysisSchemaUrl } from '@interfaces/cmModule/schemaConfig';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { EMIMETypes } from '@enums/common';
import { EErrorCodes } from '@enums/errorCodes';

import { ANALYSIS_SCHEMA } from '@constants/queryKeys';

interface IExportAlarmHistoryReturnType {
	getAnalysisSchema: UseMutateFunction<IAnalysisSchemaUrl, any, TAnalysisSchemaDTO, any>;
	analysisSchema: IAnalysisSchemaUrl | null;
	isLoadingAnalysisSchema: boolean;
}

interface IUseAttachFilesProps {
	setAnalyzedConfigs: Dispatch<SetStateAction<IAnalysisSchemaUrl[]>>;
	setIsConfirmModals: Dispatch<SetStateAction<boolean>>;
	setErrorAnalysisSchema: Dispatch<SetStateAction<string | undefined>>;
	isOranVendor: boolean;
}

export const useMutateAnalysisSchemaFiles = ({
	setAnalyzedConfigs,
	setIsConfirmModals,
	setErrorAnalysisSchema,
	isOranVendor,
}: IUseAttachFilesProps): IExportAlarmHistoryReturnType => {
	const queryFn = isOranVendor ? analysisOranFiles : analysisFiles;

	const {
		data: analysisSchema,
		mutate: getAnalysisSchema,
		isLoading: isLoadingAnalysisSchema,
	} = useMutation<TAnalysisSchemaDTO, IAnalysisSchemaUrl>({
		mutationKey: [ANALYSIS_SCHEMA],
		mutationFn: queryFn,
		onSuccess: (data, params) => {
			if (params.file?.type === EMIMETypes.zip) {
				setAnalyzedConfigs([
					{
						...data,
						importId: params.importId,
						file: params.file,
					},
				]);

				if (data.items.length) {
					setIsConfirmModals(true);
				}
			} else {
				setAnalyzedConfigs(prevState => ([
					...prevState,
					{
						...data,
						importId: params.importId,
						file: params.file,
					},
				]));

				if (data.items.length) {
					setIsConfirmModals(true);
				}
			}

			setErrorAnalysisSchema(undefined);
		},
		onError: (err: IResponseError) => {
			if (!err.errors.find((item) => item.code === EErrorCodes.assetSubTypeNameMustBeUnique)) {
				handleErrorFromMessage(err);
			}

			setErrorAnalysisSchema(err.errors[0].message);
		},
	});

	return {
		analysisSchema: analysisSchema || null,
		getAnalysisSchema,
		isLoadingAnalysisSchema,
	};
};
